/**
 * @nrx/error-tracking-web — Sentry for browser / React apps.
 *
 * The browser twin of @nrx/error-tracking: same PHI scrubbing
 * (scrubPhiFromSentryEvent, from the runtime-agnostic phi-scrub module) but built
 * on @sentry/react and configured for the browser. There is no OTel trace context
 * (the browser has none) and no Secrets Manager — the DSN is public and injected
 * at build time via VITE_SENTRY_DSN.
 *
 * Usage — at the very top of each app's src/main.tsx, before createRoot():
 *   import { initSentryBrowser } from '@nrx/error-tracking-web';
 *   initSentryBrowser({
 *     dsn: import.meta.env.VITE_SENTRY_DSN,
 *     environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
 *     release: import.meta.env.VITE_SENTRY_RELEASE,
 *     app: 'admin-portal',
 *   });
 */
import { scrubPhiFromSentryEvent, type SentryEvent } from '@nrx/error-tracking/phi-scrub';
import * as Sentry from '@sentry/react';

// Re-export the Sentry namespace so apps can use Sentry.ErrorBoundary etc. without
// adding @sentry/react to their own deps.
export { Sentry };

export interface InitSentryBrowserOptions {
  /** Public DSN (VITE_SENTRY_DSN). Empty/undefined ⇒ Sentry disabled (no-op). */
  readonly dsn: string | undefined;
  /** App name → set as the `app` tag so the shared nrx-frontend project filters per app. */
  readonly app: string;
  readonly environment?: string | undefined;
  readonly release?: string | undefined;
  readonly tracesSampleRate?: number;
}

/**
 * Initialises Sentry in a browser app. No-op (returns false) when no DSN is set,
 * mirroring the backend's "missing DSN ⇒ disabled" contract so local dev and
 * un-provisioned builds boot cleanly. Returns true when Sentry was initialised.
 */
export function initSentryBrowser(options: InitSentryBrowserOptions): boolean {
  if (!options.dsn) return false;
  Sentry.init({
    dsn: options.dsn,
    environment: options.environment ?? 'sandbox',
    ...(options.release ? { release: options.release } : {}),
    tracesSampleRate: options.tracesSampleRate ?? 0.1,
    initialScope: { tags: { app: options.app } },
    // Cast through any — our SentryEvent is a structural subset of Sentry's Event.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    beforeSend: ((event: SentryEvent) => scrubPhiFromSentryEvent(event)) as any,
  });
  return true;
}
